<!-- CreditPlanTable.vue -->
<template>
	<div class="credit-plan-table-container">
		<!-- Header with Title and Info -->
		<div class="d-flex align-items-center justify-content-between mb-4">
			<div>
				<h5 class="fw-bold mb-2">{{ title }}</h5>
				<p class="text-muted small mb-0">{{ subtitle }}</p>
			</div>
		</div>

		<!-- Credit Plan Summary Cards -->
		<div class="row g-3 mb-4" v-if="currentCreditData">
			<div class="col-md-3">
				<div class="card border-0  h-100">
					<div class="card-body p-3">
						<div class="small text-muted text-uppercase fw-normal mb-2">Total Amount</div>
						<div class="fw-bold fs-5">{{ currentCreditData.totalAmount?.formatted || 'N/A' }}</div>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card border-0  h-100">
					<div class="card-body p-3">
						<div class="small text-muted text-uppercase fw-normal mb-2">Per Repayment</div>
						<div class="fw-bold fs-5">{{ currentCreditData.repaymentPerSwap?.formatted || 'N/A' }}</div>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card border-0  h-100">
					<div class="card-body p-3">
						<div class="small text-muted text-uppercase fw-normal mb-2">Remaining Balance</div>
						<div class="fw-bold fs-5">{{ currentCreditData.balance?.formatted || 'N/A' }}</div>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card border-0  h-100">
					<div class="card-body p-3">
						<div class="small text-muted text-uppercase fw-normal mb-2">Repayments Sequence</div>
						<div class="fw-bold fs-5">{{ currentCreditData.paidSequence}} / {{ currentCreditData.numberOfRepayments }}</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Slot for injected content after summary -->
		<slot name="after-summary"></slot>

		<!-- Repayment Schedule Table -->
		<div class="table-responsive bg-white">
			<table class="table table-hover">
				<thead class="table-light">
					<tr>
						<th>Sequence #</th>
						<th>Amount</th>
						<th>Status</th>
						<th>Paid Date</th>
						<th class="text-center">Collect on Swap</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(repayment, index) in paginatedRepayments" :key="index">
						<td class="fw-semibold">{{ repayment.number }}</td>
						<td>₦{{ formatCurrency(repayment.amount) }}</td>
						<td>
							<span 
								class="badge px-3 py-2 rounded-pill"
								:class="repayment.status === 'Paid' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'"
							>
								{{ repayment.status }}
							</span>
						</td>
						<td>
							<span v-if="repayment.paidDate" class="small text-muted">
								{{ formatDate(repayment.paidDate) }}
							</span>
							<span v-else class="text-muted">-</span>
						</td>
						<td class="text-center">
							<div v-if="repayment.isActive && repayment.status === 'Pending'" class="form-check form-switch d-flex justify-content-center">
								<input 
									class="form-check-input" 
									type="checkbox" 
									:id="'toggle-' + repayment.number"
									v-model="repayment.collectRepayment"
									@change="handleToggleChange(repayment)"
									:disabled="isLoading"
								>
							</div>
							<span v-else class="text-muted">-</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Pagination Controls -->
		<div class="d-flex align-items-center justify-content-between mt-4">
			<div class="text-muted small">
				Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalRepayments) }} of {{ totalRepayments }}
			</div>
			<div class="d-flex gap-2">
				<button 
					@click="previousPage" 
					:disabled="currentPage === 1 || isLoading"
					class="btn btn-sm btn-outline-secondary rounded-2"
				>
					<i class="fas fa-chevron-left me-1"></i>Previous
				</button>
				<div class="d-flex align-items-center gap-2">
					<span class="small text-muted">Page</span>
					<select v-model.number="currentPage" :disabled="isLoading" class="form-select form-select-sm rounded-2" style="width: auto;">
						<option v-for="page in totalPages" :key="page" :value="page">{{ page }}</option>
					</select>
					<span class="small text-muted">of {{ totalPages }}</span>
				</div>
				<button 
					@click="nextPage" 
					:disabled="currentPage === totalPages || isLoading"
					class="btn btn-sm btn-outline-secondary rounded-2"
				>
					Next<i class="fas fa-chevron-right ms-1"></i>
				</button>
			</div>
		</div>

		<!-- Items Per Page Selector -->
		<div class="d-flex align-items-center gap-2 mt-3">
			<label class="small text-muted mb-0">Items per page:</label>
			<select v-model.number="itemsPerPage" :disabled="isLoading" class="form-select form-select-sm rounded-2" style="width: auto;">
				<option value="5">5</option>
				<option value="10">10</option>
				<option value="15">15</option>
				<option value="25">25</option>
			</select>
		</div>
	</div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'
import Swal from 'sweetalert2'

interface Repayment {
	number: number
	amount: number
	status: 'Pending' | 'Paid'
	isActive: boolean
	collectRepayment: boolean
	paidDate?: string
}

interface CreditData {
	id: string | number
	totalAmount: { formatted: string; amount: string }
	repaymentPerSwap: { formatted: string; amount: string }
	numberOfRepayments: number
	paidSequence: number
	skippedSequence: number
	balance: { formatted: string; amount: string }
	repaymentHistory?: Array<{ paidSequence: number; paidAt?: string; skippedAt?: string }>
}

@Component
class CreditPlanTable extends Vue {
	@Prop({ type: Object, required: false })
	readonly creditData!: CreditData

	@Prop({ type: String, required: true })
	readonly title!: string

	@Prop({ type: String, required: true })
	readonly subtitle!: string

	@Prop({ type: String, required: true })
	readonly updateEndpoint!: string

	@Prop({ type: String, required: false })
	readonly fetchEndpoint!: string

	@Prop({ type: Number, required: false })
	readonly riderId!: number

	@Prop({ type: String, required: false })
	readonly creditType!: string // 'credit' or 'credit_repair'

	repaymentSchedule: Repayment[] = []
	currentPage = 1
	itemsPerPage = 10
	isLoading = false
	currentCreditData: CreditData | null = null

	mounted() {
		if (this.fetchEndpoint && this.riderId) {
			this.fetchCreditData()
		} else if (this.creditData) {
			this.currentCreditData = this.creditData
			this.buildRepaymentSchedule()
		}
	}

	watch = {
		creditData: {
			handler(newVal) {
				if (newVal) {
					this.currentCreditData = newVal
					this.buildRepaymentSchedule()
				}
			},
			deep: true,
		},
	}

	async fetchCreditData() {
		this.isLoading = true
		try {
			const response = await this.$http.get(this.route(this.fetchEndpoint, { rider: this.riderId }))
			const creditType = this.creditType || 'credit'
			
			if (response.data?.data?.[creditType]) {
				this.currentCreditData = response.data.data[creditType]
				this.buildRepaymentSchedule()
			}
		} catch (error) {
			console.error('Error fetching credit data:', error)
		} finally {
			this.isLoading = false
		}
	}

	buildRepaymentSchedule() {
		if (!this.currentCreditData) return

		this.repaymentSchedule = []
		let remainingAmount = parseInt(this.currentCreditData.totalAmount.amount) || 0
		const repaymentPerSwap = parseInt(this.currentCreditData.repaymentPerSwap.amount) || 0

		for (let i = 1; i <= this.currentCreditData.numberOfRepayments; i++) {
			const repaymentAmount =
				i === this.currentCreditData.numberOfRepayments && remainingAmount < repaymentPerSwap
					? remainingAmount
					: repaymentPerSwap

			// Find paid date from repayment history
			const historyItem = this.currentCreditData.repaymentHistory?.find(
				(h: any) => h.paidSequence === i && h.paidAt
			)

			this.repaymentSchedule.push({
				number: i,
				amount: repaymentAmount,
				status: i <= this.currentCreditData.paidSequence ? 'Paid' : 'Pending',
				isActive: i === this.currentCreditData.paidSequence + 1,
				collectRepayment: i !== this.currentCreditData.skippedSequence,
				paidDate: historyItem?.paidAt,
			})

			remainingAmount -= repaymentAmount
		}
	}

	get totalRepayments(): number {
		return this.repaymentSchedule.length
	}

	get totalPages(): number {
		return Math.ceil(this.totalRepayments / this.itemsPerPage)
	}

	get startIndex(): number {
		return (this.currentPage - 1) * this.itemsPerPage
	}

	get endIndex(): number {
		return this.startIndex + this.itemsPerPage
	}

	get paginatedRepayments(): Repayment[] {
		return this.repaymentSchedule.slice(this.startIndex, this.endIndex)
	}

	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--
		}
	}

	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++
		}
	}

	formatCurrency(amount: number): string {
		return new Intl.NumberFormat('en-NG').format(amount / 100)
	}

	formatDate(date: string): string {
	if (!date) return '-'
	const dateObj = new Date(date)
	return dateObj.toLocaleString('en-NG', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit', // optional — remove if you don’t want seconds
		hour12: true // use 12-hour format; set to false for 24-hour format
	})
}


	async handleToggleChange(repayment: Repayment) {
		this.isLoading = true

		try {
			const payload = {
				repayment_sequence: repayment.number,
				collectRepayment: repayment.collectRepayment,
			}

			// Use the endpoint prop passed from parent
			const creditId = this.currentCreditData?.id
			
			this.creditType === "credit" ? await this.$http.post(this.route(this.updateEndpoint, { credit: creditId }), payload) :
			await this.$http.post(this.route(this.updateEndpoint, { creditrepair: creditId }), payload)

			Swal.fire({
				title: 'Success!',
				text: `Repayment ${repayment.number} has been ${repayment.collectRepayment ? 'activated' : 'skipped'}.`,
				icon: 'success',
				confirmButtonText: 'OK',
			})

			this.$emit('toggle-success', repayment)

			setTimeout(()=>{
				this.$inertia.visit(document.referrer, { preserveScroll: true })
			},3000)
		} catch (error) {
			console.error('Error updating toggle state:', error)
			repayment.collectRepayment = !repayment.collectRepayment // Revert toggle
			Swal.fire({
				title: 'Error!',
				text: 'Failed to update repayment settings. Please try again.',
				icon: 'error',
				confirmButtonText: 'OK',
			})
		} finally {
			this.isLoading = false
		}
	}
}

export default toNative(CreditPlanTable)
</script>

<style scoped>
.credit-plan-table-container {
	padding: 0;
}

.table th {
	border-top: none;
	font-weight: 600;
	color: #6c757d;
	font-size: 0.875rem;
}

.table td {
	border-color: #f8f9fa;
	vertical-align: middle;
}

.table-hover tbody tr:hover {
	background-color: rgba(102, 126, 234, 0.05);
}

.form-check-input:checked {
	background-color: #198754;
	border-color: #198754;
}

.form-check-input:focus {
	border-color: #86b7fe;
	outline: 0;
	box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.form-check-input:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.btn:disabled {
	opacity: 0.65;
	cursor: not-allowed;
}

/* Custom scrollbar */
.table-responsive::-webkit-scrollbar {
	height: 6px;
}

.table-responsive::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 10px;
}

.table-responsive::-webkit-scrollbar-thumb {
	background: #667eea;
	border-radius: 10px;
}
</style>